/**
 * Reply to a client from the messages page (Lloyd, 14 Oct 2026: "a message page that shows all
 * incoming messages as well as all AI generated and sent responses ... When AI answers it, it should
 * show that it was sent and be copied to the client's file.").
 *
 * Opens with the client's latest message and a reply drafted from it and their open cases. A person
 * reads it, edits it and presses Send; nothing goes by itself. Sending uses the same route as "Send
 * update", so it goes by email, text (where the client agreed) and portal, and is recorded on each of
 * the client's open cases.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ClientUpdateApi from '../../api/ClientUpdateApi';

interface MessageReplyDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful send, so the list can move this conversation to "Answered". */
  onSent: () => void;
  client: { guid: string; name: string } | null;
}

const MessageReplyDialog: React.FC<MessageReplyDialogProps> = ({ open, onClose, onSent, client }) => {
  const [clientMessage, setClientMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [aiDrafted, setAiDrafted] = useState<boolean | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentNote, setSentNote] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !client) return;
    let cancelled = false;
    setClientMessage('');
    setSubject('');
    setBody('');
    setAiDrafted(null);
    setError(null);
    setSentNote(null);
    setDrafting(true);
    ClientUpdateApi.replyDraft(client.guid)
      .then((draft) => {
        if (cancelled) return;
        setClientMessage(draft.clientMessage);
        setSubject(draft.subject);
        setBody(draft.body);
        setAiDrafted(draft.aiDrafted);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not draft the reply');
      })
      .finally(() => {
        if (!cancelled) setDrafting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, client]);

  const send = async () => {
    if (!client) return;
    setSending(true);
    setError(null);
    try {
      const delivery = await ClientUpdateApi.send(client.guid, subject.trim(), body.trim(), []);
      const channels = [
        delivery.email ? 'email' : null,
        delivery.text.sent ? 'text' : null,
        'portal',
      ].filter(Boolean);
      setSentNote(
        `Sent by ${channels.join(', ')}${
          delivery.citations.length > 0 ? `, and recorded on ${delivery.citations.join(', ')}` : ''
        }.`,
      );
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Reply to {client?.name || 'client'}</DialogTitle>
      <DialogContent>
        {drafting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Drafting a reply…</Typography>
          </Box>
        ) : (
          <>
            {clientMessage && (
              <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1.5, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Their message
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {clientMessage}
                </Typography>
              </Box>
            )}
            {aiDrafted === false && !sentNote && (
              <Alert severity="info" sx={{ mb: 2 }}>
                The AI could not draft this one, so a short holding reply is filled in. Edit it before
                sending.
              </Alert>
            )}
            <TextField
              label="Subject"
              size="small"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending || !!sentNote}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Reply"
              fullWidth
              multiline
              minRows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending || !!sentNote}
              helperText="Read and edit before sending. It goes by email, text (if they agreed) and portal, and is recorded on the client's open cases."
            />
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {sentNote && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {sentNote}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{sentNote ? 'Close' : 'Cancel'}</Button>
        {!sentNote && (
          <Button
            variant="contained"
            onClick={send}
            disabled={drafting || sending || !subject.trim() || !body.trim()}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MessageReplyDialog;
